import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { IDemographics } from 'app/models/core/lab/demographics';
import { IChangeLog, ItempDto } from 'app/models/core/lab/changeLog';
import { ILicenseCash } from 'app/models/core/lab/licenseCash';
import { IAddProficiency, IProficiency, IProficiencyResult } from 'app/models/core/lab/proficiency';
import { IEvaluators } from 'app/models/core/home/evaluators';
import { IPtTests } from 'app/models/core/home/ptTests';
import { ILabSpecialty, ILabSpecialtyList } from 'app/models/core/lab/specialty';
import { IAssociated, IAssociatedData, IParentList } from 'app/models/core/lab/associated';
import { Router } from '@angular/router';
import { ISurvey } from 'app/models/core/lab/surveys';
import { AuthService } from '@shared/auth/auth.service';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache'
  }),
  withCredentials: true
};

@Injectable({ providedIn: 'root' })

export class LabService {

  private api: string = environment.privateApi + 'Lab/';

  public certType = '';
  public lab: IDemographics = null;
  public associated: IAssociatedData = null;
  public associatedTab = 0;
  public totalVolume = 0;
  public readonly = false;

  currentTab = 0;

  setupDemographics(stateNumber: string) {
    this.getLab(stateNumber).subscribe(
      res => {
        this.lab = res;
        this.getAssociatedLab(stateNumber).subscribe(
          assoc => {
            this.associated = assoc;
            this.router.navigate(['lab/']);
          },
          error => console.log('error', error),
          () => {
          }
        );
      },
      error => console.log('error', error),
      () => {
      }
    );
  }

  setupAssociated(stateNumber: string){
    this.getAssociatedLab(stateNumber).subscribe(
      assoc => {
        this.associated = assoc;
      },
      error => console.log('error', error),
      () => {
      }
    );
  }

  constructor(private http: HttpClient,
    private router: Router,
    private authService: AuthService
  ) { 
    this.readonly = this.authService.userHasAtLeastOneRole(['ReadOnly']);
  }

  // ******************** Demographics ********************
  getLab(labId: string): Observable<IDemographics> {
    return this.http.get<IDemographics>(this.api + 'demographics/' + labId
      , { withCredentials: true });
  }

  saveLabDemographics(lab: IDemographics): Observable<any> {
    return this.http.post(this.api + 'saveLabDemographics', lab, httpOptions)
  }

  printDemog(labId: string): Observable<Blob> {
    return this.http.get<Blob>(this.api + 'printDemog/' + labId
      , { responseType: 'blob' as 'json', withCredentials: true });
  }

  // ******************** Surveys ************************

  getSurveys(labId: string): Observable<ISurvey[]> {
   return this.http.get<ISurvey[]>(this.api + 'getSurveys/' + labId
      , { withCredentials: true });
  }

  saveSurvey(survey: ISurvey): Observable<any> {
    return this.http.post(this.api + 'saveSurvey', survey, httpOptions)
  }

  deleteSurvey(surveyId: number): Observable<any> {
    return this.http.get(this.api + 'deleteSurvey/' + surveyId
      , { withCredentials: true });
  }

  surveyReport(id:number): Observable<Blob> {
    return this.http.get<Blob>(this.api + 'surveyReport/' + id
    , { responseType: 'blob' as 'json', withCredentials: true });
  }

  // ******************** Associated *********************

  getParentList(search: string): Observable<IParentList[]> {
    return this.http.get<IParentList[]>(this.api + 'getParentList/' + search
      , { withCredentials: true });
  }

  getAssociatedLab(id: string): Observable<IAssociatedData> {
    return this.http.get<IAssociatedData>(this.api + 'getAssociatedLab/' + id
      , { withCredentials: true });
  }

  saveAssociatedLab(save: IAssociated, type: string): Observable<any> {
    return this.http.post(this.api + 'saveAssociatedLab/' + type, save, httpOptions);
  }

  deleteAssociated(id: number, type: string): Observable<any> {
    return this.http.get(this.api + 'deleteAssociated/' + id + '/' + type, { withCredentials: true });
  }

  getHstList(cliaNumber: string): Observable<any> {
    return this.http.get(this.api + 'getHstList/' + cliaNumber, { withCredentials: true });
  }

  getSoaList(search: string): Observable<IAssociated[]> {
    return this.http.get<IAssociated[]>(this.api + 'getSoaList/' + search, { withCredentials: true });
  }

  getSharedAssociatedList(search: string): Observable<IAssociated[]> {
    return this.http.get<IAssociated[]>(this.api + 'getSharedAssociatedList/' + search, { withCredentials: true });
  }

  getMultipleList(search: string): Observable<IAssociated[]> {
    return this.http.get<IAssociated[]>(this.api + 'getMultipleList/' + search, { withCredentials: true });
  }

  // ******************** Specialty *********************

  getLabSpecialtyList(stateNum: string): Observable<ILabSpecialty[]> {
    return this.http.get<ILabSpecialty[]>(this.api + 'getLabSpecialtyList/' + stateNum
      , { withCredentials: true });
  }

  getSpecialtyList(): Observable<ILabSpecialtyList[]> {
    return this.http.get<ILabSpecialtyList[]>(this.api + 'getSpecialtyList'
      , { withCredentials: true });
  }

  saveLabSpecialty(result: ILabSpecialty): Observable<any> {
    return this.http.post(this.api + 'saveLabSpecialty', result, httpOptions);
  }

  deleteLabSpecialty(id: string): Observable<any> {
    return this.http.get(this.api + 'deleteLabSpecialty/' + id, { withCredentials: true });
  }

  // ******************** Proficiency *********************

  getProficiencyList(labId: string): Observable<IProficiency[]> {
    return this.http.get<IProficiency[]>(this.api + 'getProficiency/' + labId
      , { withCredentials: true });
  }

  savePtResult(result: IProficiencyResult): Observable<any> {
    return this.http.post(this.api + 'saveProficiencyResult', result, httpOptions);
  }

  deletePtResult(id: number): Observable<any> {
    return this.http.get(this.api + 'deleteProficiencyResult/' + id, { withCredentials: true });
  }

  addLabProficiency(prof: IAddProficiency): Observable<any> {
    return this.http.post(this.api + 'addLabProficiency', prof, httpOptions);
  }

  getEvaluators(): Observable<IEvaluators[]> {
    return this.http.get<IEvaluators[]>(this.api + 'getEvaluators', { withCredentials: true });
  }

  getProficiencyTests(): Observable<IPtTests[]> {
    return this.http.get<IPtTests[]>(this.api + 'getProficiencyTests', { withCredentials: true });
  }

  // ******************** License Cash ********************
  getLicenseCash(labId: string): Observable<ILicenseCash[]> {
    return this.http.get<ILicenseCash[]>(this.api + 'licenseCash/' + labId, { withCredentials: true });
  }

  saveLicenseCash(license: ILicenseCash): Observable<any> {
   return this.http.post(this.api + 'saveLicenseCash', license, httpOptions);
  }

  deleteLicenseCash(id: string): Observable<any> {
    return this.http.get(this.api + 'deleteLicenseCash/' + id, { withCredentials: true });
  }

  // ******************** Change Log ********************
  getChangeLog(labId: string): Observable<IChangeLog[]> {
    return this.http.get<IChangeLog[]>(this.api + 'changeLog/' + labId, { withCredentials: true });
  }

}
